import { useState } from "react";
import dayjs from "dayjs";
import { PrimaryButton } from "@normasteaching/norma-ui";
import {
  BonificoOrderDTO,
  BulkInvoiceIssueResultDTO,
  CalendarDate,
  externalSettlementTitle,
  getBonificoOrderErrorMessage,
} from "../../ProductService/BonificoOrder/BonificoOrderDTO.ts";
import { awaitingInvoiceOrdersQuery } from "../../ProductService/BonificoOrder/BonificoOrdersListQuery.ts";
import { useBonificoOrderInvoicing } from "../../ProductService/BonificoOrder/useBonificoOrderInvoicing.ts";
import { useBonificoOrdersPage } from "../../ProductService/BonificoOrder/useBonificoOrdersPage.ts";
import { classNames } from "../../UI/Utils/classNames.ts";
import { SettlementBadge } from "./SettlementBadge.tsx";
import { Article7TerBadge } from "./Article7TerBadge.tsx";
import { isArticle7Ter } from "../../ProductService/BonificoOrder/article7Ter.ts";
import {
  groupAwaitingInvoicesByValueDate,
  ValueDateInvoices,
} from "./awaitingInvoicesByValueDate.ts";
import { OverlayDialog } from "../../UI/Modal/OverlayDialog.tsx";

function formatDate(date: string): string {
  return dayjs(date).format("DD/MM/YYYY");
}

function formatEuros(amountInCents: number): string {
  return `€${(amountInCents / 100).toFixed(2)}`;
}

function invoicesLabel(count: number): string {
  return count === 1 ? "1 fattura" : `${count} fatture`;
}

function customerNameOf(order: BonificoOrderDTO): string {
  return (
    order.businessInformation?.businessName ||
    `${order.customer.firstName} ${order.customer.lastName}`
  );
}

function ValueDatePicker({
  groups,
  selectedValueDate,
  onSelect,
}: {
  groups: ValueDateInvoices[];
  selectedValueDate: CalendarDate;
  onSelect: (valueDate: CalendarDate) => void;
}) {
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-2 text-sm font-medium text-gray-700">
        Data valuta
      </legend>
      <div className="flex flex-wrap gap-2">
        {groups.map(({ valueDate, orders }) => {
          const isSelected = valueDate === selectedValueDate;
          return (
            <label
              key={valueDate}
              className={classNames(
                "flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary/40",
                isSelected
                  ? "border-primary bg-primary text-white"
                  : "border-gray-300 bg-white text-gray-700 hover:border-gray-500",
              )}
            >
              <input
                type="radio"
                name="value-date"
                value={valueDate}
                checked={isSelected}
                onChange={() => onSelect(valueDate)}
                className="sr-only"
              />
              {formatDate(valueDate)}
              <span
                className={classNames(
                  "min-w-6 rounded-full px-1.5 text-center text-xs font-semibold",
                  isSelected
                    ? "bg-white/25 text-white"
                    : "bg-gray-100 text-gray-600",
                )}
              >
                {orders.length}
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

function InvoiceSummaryRow({ order }: { order: BonificoOrderDTO }) {
  return (
    <li className="flex items-center gap-3 px-3 py-2 text-sm">
      <span className="min-w-0 flex-1 truncate">
        <span className="text-gray-900">{customerNameOf(order)}</span>
        <span className="text-gray-500"> · {order.customer.email}</span>
      </span>
      {isArticle7Ter(order.vatExemption) && <Article7TerBadge />}
      {order.invoiceItemDescription && (
        <span
          title={order.invoiceItemDescription}
          className="px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap bg-sky-50 text-sky-800 border border-sky-300"
        >
          Descrizione personalizzata
        </span>
      )}
      {order.installmentsSettlement && <SettlementBadge />}
      {order.externalSettlement && (
        <SettlementBadge
          label={externalSettlementTitle(order.externalSettlement)}
        />
      )}
      <span className="shrink-0 font-medium text-gray-900">
        {formatEuros(order.amountPaidInCents?.amount ?? 0)}
      </span>
    </li>
  );
}

function BulkIssueOutcome({
  result,
  ordersById,
}: {
  result: BulkInvoiceIssueResultDTO;
  ordersById: Map<string, BonificoOrderDTO>;
}) {
  const failedOrder = result.failure
    ? ordersById.get(result.failure.orderId)
    : undefined;
  return (
    <div className="flex flex-col gap-2 text-sm">
      <p className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-green-800">
        Fatture emesse: <strong>{result.issuedOrderIds.length}</strong>
      </p>
      {result.failure && (
        <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-800">
          Emissione interrotta all'ordine{" "}
          {failedOrder ? customerNameOf(failedOrder) : result.failure.orderId}:{" "}
          {result.failure.message}
          {result.notIssuedOrderIds.length === 1 &&
            " Non è stata emessa la fattura successiva."}
          {result.notIssuedOrderIds.length > 1 &&
            ` Non sono state emesse le ${result.notIssuedOrderIds.length} fatture successive.`}
        </p>
      )}
      {result.skippedOrderIds.length > 0 && (
        <p className="text-xs text-gray-500">
          {result.skippedOrderIds.length === 1
            ? "1 ordine non aveva più una fattura da emettere ed è stato saltato."
            : `${result.skippedOrderIds.length} ordini non avevano più una fattura da emettere e sono stati saltati.`}
        </p>
      )}
    </div>
  );
}

export function BulkIssueInvoicesModal({
  onDone,
  onClose,
}: {
  onDone: () => void;
  onClose: () => void;
}) {
  const { orders, totalCount, isLoading } = useBonificoOrdersPage(
    awaitingInvoiceOrdersQuery(),
  );
  const { issueInvoices, loading } = useBonificoOrderInvoicing();
  const [chosenValueDate, setChosenValueDate] = useState<CalendarDate>();
  const [result, setResult] = useState<BulkInvoiceIssueResultDTO | null>(null);
  const [error, setError] = useState<string | null>(null);

  const groups = groupAwaitingInvoicesByValueDate(orders);
  const selectedGroup =
    groups.find((group) => group.valueDate === chosenValueDate) ?? groups[0];
  const earlierGroups = selectedGroup
    ? groups.filter((group) => group.valueDate < selectedGroup.valueDate)
    : [];
  const earlierInvoicesCount = earlierGroups.reduce(
    (sum, group) => sum + group.orders.length,
    0,
  );

  const handleConfirm = async () => {
    if (!selectedGroup) return;
    setError(null);
    try {
      setResult(
        await issueInvoices(selectedGroup.orders.map((order) => order.orderId)),
      );
    } catch (e) {
      setError(
        getBonificoOrderErrorMessage(
          e,
          "Errore durante l'emissione delle fatture. Riprova.",
        ),
      );
    }
  };

  return (
    <OverlayDialog
      onClose={result ? onDone : onClose}
      isClosable={!loading}
      panelClassName="bg-white rounded-2xl shadow-xl p-6 w-full max-w-2xl max-h-[85vh] flex flex-col gap-4"
    >
      <h2 className="text-xl font-bold">Emetti fatture</h2>
      {result ? (
        <BulkIssueOutcome
          result={result}
          ordersById={
            new Map(orders.map((order) => [order.orderId, order] as const))
          }
        />
      ) : isLoading ? (
        <p className="text-sm text-gray-500">Caricamento...</p>
      ) : !selectedGroup ? (
        <p className="text-sm text-gray-500">
          Nessun ordine con fattura da emettere.
        </p>
      ) : (
        <>
          <p className="text-sm text-gray-600">
            Fatture da emettere:{" "}
            <strong className="text-gray-900">{totalCount}</strong> in totale,
            su {groups.length === 1 ? "1 data" : `${groups.length} date`}{" "}
            valuta. Scegli la data valuta di cui emettere le fatture.
          </p>
          <ValueDatePicker
            groups={groups}
            selectedValueDate={selectedGroup.valueDate}
            onSelect={setChosenValueDate}
          />
          <div className="flex min-h-0 flex-col gap-2">
            <p className="text-sm text-gray-900">
              <strong>{invoicesLabel(selectedGroup.orders.length)}</strong> con
              data valuta {formatDate(selectedGroup.valueDate)} ·{" "}
              {formatEuros(selectedGroup.totalAmountInCents)}
            </p>
            <ul className="min-h-0 overflow-y-auto divide-y divide-gray-100 rounded-lg border border-gray-200">
              {selectedGroup.orders.map((order) => (
                <InvoiceSummaryRow key={order.orderId} order={order} />
              ))}
            </ul>
          </div>
          {earlierGroups.length > 0 && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Restano da emettere {invoicesLabel(earlierInvoicesCount)} con data
              valuta precedente (dal {formatDate(earlierGroups[0].valueDate)}):
              emettile prima, altrimenti non potranno più essere fatturate con
              la loro data valuta.
            </p>
          )}
          {totalCount > orders.length && (
            <p className="text-xs text-gray-500">
              Sono considerati i primi {orders.length} ordini su {totalCount}:
              le date valuta successive compariranno dopo aver emesso queste
              fatture.
            </p>
          )}
        </>
      )}
      {error && <p className="text-sm text-red-500">{error}</p>}
      <div className="flex gap-3 justify-end">
        {result ? (
          <PrimaryButton label="Chiudi" onClick={onDone} />
        ) : (
          <>
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm border border-gray-300 hover:bg-gray-50"
            >
              Annulla
            </button>
            {selectedGroup && (
              <PrimaryButton
                label={
                  loading
                    ? "Emissione..."
                    : `Emetti ${invoicesLabel(selectedGroup.orders.length)} del ${formatDate(selectedGroup.valueDate)}`
                }
                onClick={handleConfirm}
                disabled={loading}
              />
            )}
          </>
        )}
      </div>
    </OverlayDialog>
  );
}
